import { useEffect, useState } from "react";
import { PD } from "@/constants/testIds";
import {
  TABLE_MAX_ROWS_DEFAULT,
  TABLE_MAX_ROWS_LIMIT,
  TABLE_SOURCES,
  addTableColumn,
  availableTableColumns,
  defaultColumnsForSource,
  defaultTableTitle,
  emptyTableContent,
  loadTablePreviewPatients,
  moveTableColumn,
  removeTableColumn,
  sanitizeTableContent,
  selectedTableColumns,
  tableSource,
} from "@/lib/pageDesignerTable";

const FUTURE_CONTENT = [
  { id: "form", label: "Form" },
  { id: "emr-data", label: "EMR Data" },
  { id: "smartphrase", label: "SmartPhrase" },
];

function StepDot({ n, current, label }) {
  return (
    <span className={`epd__step${current === n ? " is-active" : ""}`}>
      {n}. {label}
    </span>
  );
}

export function PageDesignerAddContentPicker({ open, onClose, onChooseTable, onChooseSearch }) {
  if (!open) return null;
  return (
    <div className="epd__modal" role="dialog" aria-modal="true" data-testid={PD.addContentDialog}>
      <div className="epd__modal-card epd__modal-card--wide">
        <h3>Add Content</h3>
        <p>Choose the functionality that belongs inside this card. The card stays the container.</p>
        <div className="epd__content-pick">
          <button
            type="button"
            className="epd__btn"
            data-testid={PD.addContentTable}
            onClick={onChooseTable}
          >
            Table
          </button>
          <button
            type="button"
            className="epd__btn"
            data-testid={PD.addContentSearch}
            onClick={onChooseSearch}
          >
            Search
          </button>
          {FUTURE_CONTENT.map((item) => (
            <button key={item.id} type="button" className="epd__btn epd__btn--ghost" disabled>
              {item.label} (Coming Soon)
            </button>
          ))}
        </div>
        <div className="epd__modal-actions">
          <button type="button" className="epd__btn epd__btn--ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PageDesignerTableBuilder({ card, open, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(emptyTableContent());
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    setStep(1);
    const current = sanitizeTableContent(card?.tableContent);
    setDraft(
      current.sourceId
        ? current
        : emptyTableContent(),
    );
    let cancelled = false;
    loadTablePreviewPatients().then((items) => {
      if (cancelled) return;
      setPatients(items);
      setDraft((current) => {
        if (current.previewPatientId || !items[0]) return current;
        return sanitizeTableContent({ ...current, previewPatientId: items[0].id });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, card?.id]);

  if (!open) return null;

  const source = tableSource(draft.sourceId);
  const available = availableTableColumns(draft);
  const selected = selectedTableColumns(draft);
  const canNextFromTable = Boolean(draft.sourceId);
  const canNextFromColumns = draft.columns.length > 0;
  const canSave = canNextFromTable && canNextFromColumns;

  const patch = (partial) => setDraft((current) => sanitizeTableContent({ ...current, ...partial }));

  const chooseSource = (sourceId) => {
    const nextTitle =
      draft.title.trim() && draft.title !== defaultTableTitle(draft.sourceId)
        ? draft.title
        : defaultTableTitle(sourceId);
    const next = sanitizeTableContent({
      ...emptyTableContent(),
      sourceId,
      title: nextTitle,
      columns: defaultColumnsForSource(sourceId),
      previewPatientId: draft.previewPatientId,
      maxRows: draft.maxRows || TABLE_MAX_ROWS_DEFAULT,
    });
    setDraft(next);
  };

  const save = () => {
    if (!canSave) return;
    onSave(sanitizeTableContent(draft));
  };

  return (
    <div className="epd__modal" role="dialog" aria-modal="true" data-testid={PD.tableBuilder}>
      <div className="epd__modal-card epd__modal-card--wide">
        <h3>Table Builder</h3>
        <p>Configure table content for {card?.title || "this card"}. The card remains the container.</p>
        <div className="epd__steps">
          <StepDot n={1} current={step} label="Choose Table" />
          <StepDot n={2} current={step} label="Choose Columns" />
          <StepDot n={3} current={step} label="Settings" />
        </div>

        {step === 1 ? (
          <label className="epd__field" htmlFor="epd-table-source">
            <span className="epd__label">Choose Table</span>
            <select
              id="epd-table-source"
              data-testid={PD.tableSource}
              className="epd__select browser-default"
              value={draft.sourceId}
              onChange={(event) => chooseSource(event.target.value)}
            >
              <option value="">Select table</option>
              {TABLE_SOURCES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            {source ? (
              <span className="epd__tip">
                Uses the existing {source.table} table through the current patient API. No new
                database table is created.
              </span>
            ) : (
              <span className="epd__tip">Select an existing EMRbyMD clinical data source.</span>
            )}
          </label>
        ) : null}

        {step === 2 ? (
          <div className="epd__col-board">
            <div>
              <div className="epd__label">Available Columns</div>
              <div className="epd__col-list" data-testid={PD.tableAvailable}>
                {available.length === 0 ? (
                  <p className="epd__tip">All columns are selected.</p>
                ) : (
                  available.map((column) => (
                    <button
                      key={column.id}
                      type="button"
                      className="epd__stack-item"
                      data-testid={`${PD.tableColAdd}-${column.id}`}
                      onClick={() => setDraft((current) => addTableColumn(current, column.id))}
                    >
                      <span className="epd__stack-title">{column.label}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="epd__label">Selected Columns</div>
              <div className="epd__col-list" data-testid={PD.tableSelected}>
                {selected.length === 0 ? (
                  <p className="epd__tip">Select at least one column.</p>
                ) : (
                  selected.map((column) => (
                    <div key={column.id} className="epd__col-selected">
                      <span className="epd__stack-title">{column.label}</span>
                      <span className="epd__col-actions">
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.tableColUp}-${column.id}`}
                          onClick={() => setDraft((current) => moveTableColumn(current, column.id, "up"))}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.tableColDown}-${column.id}`}
                          onClick={() =>
                            setDraft((current) => moveTableColumn(current, column.id, "down"))
                          }
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.tableColRemove}-${column.id}`}
                          onClick={() => setDraft((current) => removeTableColumn(current, column.id))}
                        >
                          Remove
                        </button>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div>
            <label className="epd__field" htmlFor="epd-table-title">
              <span className="epd__label">Title</span>
              <input
                id="epd-table-title"
                data-testid={PD.tableTitle}
                className="epd__input"
                value={draft.title}
                onChange={(event) => patch({ title: event.target.value })}
              />
            </label>
            <div className="epd__pair">
              <label className="epd__field" htmlFor="epd-table-sort">
                <span className="epd__label">Sort Column</span>
                <select
                  id="epd-table-sort"
                  data-testid={PD.tableSortColumn}
                  className="epd__select browser-default"
                  value={draft.sortColumn}
                  onChange={(event) => patch({ sortColumn: event.target.value })}
                >
                  <option value="">Source order</option>
                  {selected.map((column) => (
                    <option key={column.id} value={column.id}>
                      {column.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="epd__field" htmlFor="epd-table-dir">
                <span className="epd__label">Sort Direction</span>
                <select
                  id="epd-table-dir"
                  data-testid={PD.tableSortDirection}
                  className="epd__select browser-default"
                  value={draft.sortDirection}
                  onChange={(event) => patch({ sortDirection: event.target.value })}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </label>
            </div>
            <div className="epd__pair">
              <label className="epd__field" htmlFor="epd-table-rows">
                <span className="epd__label">Maximum displayed rows</span>
                <input
                  id="epd-table-rows"
                  data-testid={PD.tableMaxRows}
                  className="epd__input"
                  type="number"
                  min={1}
                  max={TABLE_MAX_ROWS_LIMIT}
                  value={draft.maxRows}
                  onChange={(event) => patch({ maxRows: event.target.value })}
                />
              </label>
              <label className="epd__field" htmlFor="epd-table-patient">
                <span className="epd__label">Preview patient</span>
                <select
                  id="epd-table-patient"
                  data-testid={PD.tablePreviewPatient}
                  className="epd__select browser-default"
                  value={draft.previewPatientId}
                  onChange={(event) => patch({ previewPatientId: event.target.value })}
                >
                  <option value="">Select patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="epd__tip">
              Preview patient is designer-only until this card lives on a patient page. Long-term
              patient context comes from the page, not from a separate table card type.
            </p>
          </div>
        ) : null}

        <div className="epd__modal-actions">
          <button
            type="button"
            className="epd__btn epd__btn--ghost"
            data-testid={PD.tableBuilderCancel}
            onClick={onClose}
          >
            Cancel
          </button>
          {step > 1 ? (
            <button
              type="button"
              className="epd__btn epd__btn--ghost"
              data-testid={PD.tableBuilderBack}
              onClick={() => setStep((current) => current - 1)}
            >
              Back
            </button>
          ) : null}
          {step < 3 ? (
            <button
              type="button"
              className="epd__btn"
              data-testid={PD.tableBuilderNext}
              disabled={step === 1 ? !canNextFromTable : !canNextFromColumns}
              onClick={() => setStep((current) => current + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="epd__btn"
              data-testid={PD.tableBuilderSave}
              disabled={!canSave}
              onClick={save}
            >
              Save Content
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
